<template>
  <div class="card shadow-sm order-editor">
    <div class="card-header bg-white d-flex justify-content-between align-items-center">
      <div>
        <h5 class="mb-0">{{ headerTitle }}</h5>
        <small class="text-muted">{{ headerSubtitle }}</small>
      </div>
      <span v-if="pedido?.estado" :class="['badge', badgeClass(pedido.estado)]">
        {{ pedido.estado }}
      </span>
    </div>

    <div class="card-body">
      <div v-if="readonly" class="alert alert-secondary mb-3">
        Este pedido está en solo lectura.
      </div>

      <div class="row g-2 mb-3">
        <div class="col-md-4">
          <label class="form-label">RUC / CI</label>
          <div class="input-group">
            <input
              v-model="rucCliente"
              type="text"
              class="form-control form-control-sm"
              placeholder="Ingresa el RUC/CI"
              :readonly="readonly"
              @blur="verificarRUC"
              @keyup.enter.prevent="verificarRUC"
            />
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              :disabled="readonly"
              @click="toggleBuscarCliente"
            >
              Buscar
            </button>
          </div>
        </div>
        <div class="col-md-4">
          <label class="form-label">Cliente</label>
          <input
            v-model="clienteNombre"
            type="text"
            class="form-control form-control-sm"
            placeholder="Nombre del cliente"
            :readonly="readonly"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Teléfono</label>
          <input
            v-model="clienteTelefono"
            type="text"
            class="form-control form-control-sm"
            placeholder="Teléfono"
            :readonly="readonly"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label">Dirección</label>
          <input
            v-model="clienteDireccion"
            type="text"
            class="form-control form-control-sm"
            placeholder="Dirección"
            :readonly="readonly"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label">Email</label>
          <input
            v-model="clienteEmail"
            type="text"
            class="form-control form-control-sm"
            placeholder="Email"
            :readonly="readonly"
          />
        </div>
      </div>

      <div class="card border-0 bg-light mb-3">
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <div class="col-md-5">
              <label class="form-label">Código / SKU / Barcode / Nombre</label>
              <div class="input-group">
                <input
                  v-model="productCode"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Producto o presentación"
                  :readonly="readonly"
                  @keyup.enter.prevent="buscarProducto"
                  @blur="productCode && buscarProducto()"
                />
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="readonly"
                  @click="toggleBuscarProducto"
                >
                  Buscar
                </button>
              </div>
            </div>
            <div class="col-md-2">
              <label class="form-label">Cantidad</label>
              <input
                v-model.number="productQuantity"
                type="number"
                min="1"
                class="form-control form-control-sm"
                :readonly="readonly"
                @keyup.enter.prevent="agregarProducto"
              />
            </div>
            <div class="col-md-5">
              <label class="form-label">Descripción</label>
              <div class="input-group">
                <input
                  v-model="productDescription"
                  type="text"
                  class="form-control form-control-sm"
                  readonly
                  placeholder="Descripción del producto"
                />
                <button
                  type="button"
                  class="btn btn-success btn-sm"
                  :disabled="readonly || !selectedProduct"
                  @click="agregarProducto"
                >
                  <i class="bi bi-plus"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="order-editor__mobile-list d-lg-none">
        <div v-if="!productos.length" class="card border-0 bg-light">
          <div class="card-body text-center text-muted py-4">
            No hay productos cargados.
          </div>
        </div>

        <div
          v-for="(producto, index) in productos"
          :key="`mobile-${producto.producto_id || producto.presentacion_id}-${index}`"
          class="card shadow-sm border-0 order-editor__product-card"
        >
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div class="order-editor__product-index">Ítem {{ index + 1 }}</div>
                <h6 class="mb-1 fw-bold">{{ producto.nombre || '—' }}</h6>
                <div class="text-muted small">Código: {{ producto.codigo || '—' }}</div>
                <div v-if="producto.es_presentacion" class="text-primary small fw-semibold">Presentación</div>
              </div>
              <button
                v-if="!readonly"
                type="button"
                class="btn btn-outline-danger btn-sm"
                @click="eliminarProducto(index)"
              >
                Quitar
              </button>
            </div>

            <div class="order-editor__product-grid">
              <div class="order-editor__product-cell">
                <span class="label">Cantidad</span>
                <strong>{{ formatCantidad(producto) }}</strong>
              </div>
              <div class="order-editor__product-cell">
                <span class="label">Unidad</span>
                <strong>{{ producto.unidad_medida || '—' }}</strong>
              </div>
              <div class="order-editor__product-cell">
                <span class="label">Precio Unit.</span>
                <strong>{{ formatPrecio(producto) }}</strong>
              </div>
              <div class="order-editor__product-cell">
                <span class="label">Subtotal</span>
                <strong>{{ formateaNumero(subtotalLinea(producto)) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-responsive d-none d-lg-block">
        <table class="table table-sm table-striped align-middle">
          <thead>
            <tr>
              <!-- detalle-venta-columnas-spec.md RF-1 -->
              <th>Código</th>
              <th>Nombre</th>
              <th>Cantidad</th>
              <th>Unidad de Medida</th>
              <th class="text-end">Precio</th>
              <th class="text-end">Subtotal</th>
              <th v-if="!readonly" class="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!productos.length">
              <td :colspan="readonly ? 6 : 7" class="text-center text-muted py-4">
                No hay productos cargados.
              </td>
            </tr>
            <tr v-for="(producto, index) in productos" :key="`${producto.producto_id || producto.presentacion_id}-${index}`">
              <td>{{ producto.codigo || '—' }}</td>
              <td>
                {{ producto.nombre || '—' }}
                <span v-if="producto.es_presentacion" class="badge bg-info-subtle text-info-emphasis ms-1">Presentación</span>
              </td>
              <td>{{ formatCantidad(producto) }}</td>
              <td>{{ producto.unidad_medida || '—' }}</td>
              <td class="text-end">{{ formatPrecio(producto) }}</td>
              <td class="text-end">{{ formateaNumero(subtotalLinea(producto)) }}</td>
              <td v-if="!readonly" class="text-end">
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  @click="eliminarProducto(index)"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-3 order-editor__footer">
        <div>
          <div class="text-muted small">Total del pedido</div>
          <div class="fs-4 fw-bold">{{ formateaNumero(totalAmount) }} ₲</div>
        </div>
        <div class="d-flex gap-2 flex-wrap justify-content-end">
          <button
            v-if="allowCancel"
            type="button"
            class="btn btn-outline-danger"
            :disabled="saving"
            @click="$emit('cancel-order', buildPayload())"
          >
            {{ cancelLabel }}
          </button>
          <button
            v-if="allowSavePending"
            type="button"
            class="btn btn-warning"
            :disabled="saving || readonly"
            @click="emitSavePending"
          >
            {{ pendingLabel }}
          </button>
          <button
            v-if="allowComplete"
            type="button"
            class="btn btn-success"
            :disabled="saving || readonly"
            @click="emitComplete"
          >
            {{ completeLabel }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="mostrarSelectorCliente"
      class="overlay-backdrop"
      role="dialog"
      aria-modal="true"
    >
      <div class="overlay-panel card">
        <div class="overlay-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Seleccionar Cliente</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrarSelectorCliente"></button>
        </div>
        <div class="overlay-body">
          <ClientesView
            :selectorMode="true"
            :hideHeader="true"
            @cliente-seleccionado="onClienteSeleccionado"
          />
        </div>
      </div>
    </div>

    <div
      v-if="mostrarSelectorProducto"
      class="overlay-backdrop"
      role="dialog"
      aria-modal="true"
    >
      <div class="overlay-panel card">
        <div class="overlay-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Seleccionar Producto o Presentación</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrarSelectorProducto"></button>
        </div>
        <div class="overlay-body">
          <ProductosPrecioView
            :selectorMode="true"
            :hideHeader="true"
            :initialSearch="productCode"
            @producto-seleccionado="onProductoSeleccionado"
          />
        </div>
      </div>
    </div>

    <RegistrarClienteModal
      :open="showCrearClienteModal"
      :prefillRuc="rucCliente"
      :prefillDocumento="rucCliente"
      @saved="onClienteCreadoDesdeModal"
      @close="showCrearClienteModal = false"
    />
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { resolveSaleInput } from '@/services/saleResolverService';
import salidaProtegidaService from '@/services/salidaProtegidaService';
import ClientesView from '@/views/ClientesView.vue';
import ProductosPrecioView from '@/views/Inventario/ProductosPrecioView.vue';
import RegistrarClienteModal from '@/components/RegistrarClienteModal.vue';

// detalle-venta-columnas-spec.md RF-4: peso (g) y trazabilidad de balanza de
// una línea a granel. Las líneas por unidad no lo llevan (`peso_gramos: null`).
const datosGranel = (linea = {}) => (linea.peso_gramos != null
  ? {
      peso_gramos: Number(linea.peso_gramos),
      origen_lectura: linea.origen_lectura || null,
      codigo_plu_balanza: linea.codigo_plu_balanza || null,
      codigo_balanza_raw: linea.codigo_balanza_raw || null
    }
  : { peso_gramos: null });

const emptyProduct = () => ({
  producto_id: null,
  presentacion_id: null,
  tipo_presentacion: null,
  codigo: '',
  nombre: '',
  cantidad: 1,
  unidad_medida: '',
  precio: 0,
  es_presentacion: false,
  cantidad_presentacion: null,
  cantidad_unidades: null,
  factor_conversion: null,
  componentes_stock: []
});

export default {
  name: 'OrderEditorForm',
  components: {
    ClientesView,
    ProductosPrecioView,
    RegistrarClienteModal
  },
  props: {
    pedido: {
      type: Object,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    allowSavePending: {
      type: Boolean,
      default: true
    },
    allowComplete: {
      type: Boolean,
      default: true
    },
    allowCancel: {
      type: Boolean,
      default: true
    },
    pendingLabel: {
      type: String,
      default: 'Guardar como Pendiente'
    },
    completeLabel: {
      type: String,
      default: 'Completar Pedido'
    },
    cancelLabel: {
      type: String,
      default: 'Cancelar Pedido'
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    }
  },
  emits: ['save-pending', 'complete', 'cancel-order'],
  data() {
    return {
      productCode: '',
      productQuantity: 1,
      productDescription: '',
      selectedProduct: null,
      rucCliente: '',
      clienteNombre: '',
      clienteTelefono: '',
      clienteDireccion: '',
      clienteEmail: '',
      productos: [],
      mostrarSelectorCliente: false,
      mostrarSelectorProducto: false,
      showCrearClienteModal: false,
      isVerifyingRuc: false,
      rucNotFoundForValue: '',
      isCheckingProduct: false,
      productNotFoundForCode: ''
    };
  },
  computed: {
    totalAmount() {
      // RF-2: granel por kilo, redondeado por línea como el backend.
      return Math.round(this.productos.reduce((acc, producto) => acc + this.subtotalLinea(producto), 0));
    },
    headerTitle() {
      return this.title || (this.pedido?.pedido_id ? `Pedido #${this.pedido.pedido_id}` : 'Nuevo Pedido');
    },
    headerSubtitle() {
      if (this.subtitle) return this.subtitle;
      if (!this.pedido?.pedido_id) return 'Carga el pedido y decide si queda pendiente o completado.';
      return 'Gestiona el pedido completo desde esta misma pantalla.';
    }
  },
  watch: {
    pedido: {
      immediate: true,
      deep: true,
      handler(value) {
        this.populateFromPedido(value);
      }
    },
    productCode() {
      this.selectedProduct = null;
      this.productDescription = '';
      this.productNotFoundForCode = '';
    },
    rucCliente(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rucNotFoundForValue = '';
      }
    }
  },
  methods: {
    populateFromPedido(pedido) {
      if (!pedido) {
        this.resetForm();
        return;
      }

      this.rucCliente = pedido.nro_documento || '';
      this.clienteNombre = pedido.nombre_cliente || '';
      this.clienteTelefono = pedido.telefono || '';
      this.clienteDireccion = pedido.direccion || '';
      this.clienteEmail = pedido.email || '';
      this.productos = Array.isArray(pedido.detalles)
        ? pedido.detalles.map(detalle => ({
            producto_id: detalle.producto_id,
            presentacion_id: detalle.presentacion_id || null,
            tipo_presentacion: detalle.tipo_presentacion || null,
            codigo: detalle.codigo_barras || detalle.codigo || '',
            nombre: detalle.producto_nombre || detalle.nombre || '',
            cantidad: Number(detalle.cantidad) || 0,
            unidad_medida: detalle.unidad_medida_nombre || detalle.unidad_medida || '',
            precio: Number(detalle.precio) || 0,
            es_presentacion: Boolean(detalle.presentacion_id),
            cantidad_presentacion: detalle.cantidad_presentacion || null,
            cantidad_unidades: detalle.cantidad_unidades || null,
            factor_conversion: detalle.factor_conversion || null,
            componentes_stock: detalle.componentes_stock || [],
            // RF-4: una línea a granel conserva su peso y su origen al editarse.
            ...datosGranel(detalle)
          }))
        : [];
      this.productCode = '';
      this.productQuantity = 1;
      this.productDescription = '';
      this.selectedProduct = null;
      this.huellaInicial = this.huellaPedido();
    },
    // ux-operativa-v2-spec.md RF-3: el pedido cambió si su huella difiere de
    // la que tenía al abrirse en el panel.
    huellaPedido() {
      return JSON.stringify({
        cliente: [this.rucCliente, this.clienteNombre, this.clienteTelefono, this.clienteDireccion, this.clienteEmail],
        lineas: this.productos.map((p) => [p.producto_id, p.presentacion_id, Number(p.cantidad) || 0, Number(p.precio) || 0])
      });
    },
    resetForm() {
      this.rucCliente = '';
      this.clienteNombre = '';
      this.clienteTelefono = '';
      this.clienteDireccion = '';
      this.clienteEmail = '';
      this.productos = [];
      this.productCode = '';
      this.productQuantity = 1;
      this.productDescription = '';
      this.selectedProduct = null;
    },
    buildPayload() {
      const detalles = this.productos
        .map(producto => ({
          producto_id: producto.presentacion_id ? undefined : producto.producto_id,
          presentacion_id: producto.presentacion_id || undefined,
          cantidad: Number(producto.cantidad) || 0,
          cantidad_presentacion: producto.presentacion_id ? Number(producto.cantidad) || 0 : undefined,
          ...(producto.peso_gramos != null ? {
            peso_gramos: Number(producto.peso_gramos),
            origen_lectura: producto.origen_lectura || null,
            codigo_plu_balanza: producto.codigo_plu_balanza || null,
            codigo_balanza_raw: producto.codigo_balanza_raw || null
          } : {})
        }))
        .filter(item => (item.producto_id || item.presentacion_id) && item.cantidad > 0);

      return {
        cabecera: {
          nombre_cliente: this.clienteNombre || 'Cliente sin nombre',
          nro_documento: this.rucCliente || 'S/D',
          telefono: this.clienteTelefono || '',
          direccion: this.clienteDireccion || '',
          email: this.clienteEmail || ''
        },
        detalles
      };
    },
    validatePayload() {
      const payload = this.buildPayload();
      if (!payload.detalles.length) {
        alert('Agregá al menos un producto antes de continuar.');
        return null;
      }
      if (!payload.cabecera.nombre_cliente) {
        alert('Ingresá el nombre del cliente.');
        return null;
      }
      return payload;
    },
    emitSavePending() {
      const payload = this.validatePayload();
      if (!payload) return;
      this.$emit('save-pending', payload);
    },
    emitComplete() {
      const payload = this.validatePayload();
      if (!payload) return;
      // El total va como segundo argumento y NO dentro de `payload`: el
      // payload se manda tal cual al backend y agregarle un campo cambiaría
      // el body del POST. El orquestador lo necesita para el cobro inline.
      this.$emit('complete', payload, {
        total: this.totalAmount,
        nombreCliente: payload.cabecera.nombre_cliente
      });
    },
    toggleBuscarCliente() {
      if (this.readonly) return;
      this.mostrarSelectorCliente = !this.mostrarSelectorCliente;
      document.body.style.overflow = this.mostrarSelectorCliente ? 'hidden' : '';
    },
    cerrarSelectorCliente() {
      this.mostrarSelectorCliente = false;
      document.body.style.overflow = '';
    },
    onClienteSeleccionado(cliente) {
      this.rucCliente = cliente.ruc || cliente.nro_documento || '';
      this.clienteNombre = cliente.nombre_completo || cliente.nombre_fantasia || '';
      this.clienteTelefono = cliente.telefono || '';
      this.clienteDireccion = cliente.direccion || '';
      this.clienteEmail = cliente.email || '';
      this.cerrarSelectorCliente();
    },
    onClienteCreadoDesdeModal(cli) {
      this.rucCliente = cli?.ruc || cli?.nro_documento || this.rucCliente || '';
      this.clienteNombre = cli?.nombre_completo || cli?.nombre_fantasia || this.clienteNombre || '';
      this.clienteTelefono = cli?.telefono || this.clienteTelefono || '';
      this.clienteDireccion = cli?.direccion || this.clienteDireccion || '';
      this.clienteEmail = cli?.email || this.clienteEmail || '';
      this.showCrearClienteModal = false;
      this.rucNotFoundForValue = '';
    },
    async verificarRUC() {
      if (this.readonly) return;
      const value = (this.rucCliente || '').trim();
      if (!value || this.isVerifyingRuc || this.rucNotFoundForValue === value || this.showCrearClienteModal) return;

      this.isVerifyingRuc = true;
      try {
        const { data: cliente } = await apiService.get(`/api/clients/search/${encodeURIComponent(value)}`);
        if (cliente && Object.keys(cliente).length > 0) {
          this.clienteNombre = cliente.nombre_completo || cliente.nombre_fantasia || this.clienteNombre;
          this.clienteTelefono = cliente.telefono || this.clienteTelefono;
          this.clienteDireccion = cliente.direccion || this.clienteDireccion;
          this.clienteEmail = cliente.email || this.clienteEmail;
        } else {
          this.rucNotFoundForValue = value;
          this.showCrearClienteModal = true;
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          this.rucNotFoundForValue = value;
          this.showCrearClienteModal = true;
        } else {
          alert(error.userMessage || 'No se pudo verificar el cliente.');
        }
      } finally {
        this.isVerifyingRuc = false;
      }
    },
    toggleBuscarProducto() {
      if (this.readonly) return;
      this.mostrarSelectorProducto = !this.mostrarSelectorProducto;
      document.body.style.overflow = this.mostrarSelectorProducto ? 'hidden' : '';
    },
    cerrarSelectorProducto() {
      this.mostrarSelectorProducto = false;
      document.body.style.overflow = '';
    },
    abrirSelectorProductoConBusqueda() {
      this.mostrarSelectorProducto = true;
      document.body.style.overflow = 'hidden';
    },
    onProductoSeleccionado(prod) {
      const identifier = String(prod?.codigo_barras || prod?.codigo_producto || prod?.codigo_plu_balanza || prod?.nombre || '').trim();
      if (!identifier) {
        alert('El producto seleccionado no tiene código, SKU ni nombre asociado.');
        return;
      }
      this.productCode = identifier;
      this.cerrarSelectorProducto();
      this.$nextTick(() => this.buscarProducto());
    },
    async buscarProducto() {
      if (this.readonly) return;
      const code = (this.productCode || '').trim();
      const qty = Number(this.productQuantity) || 1;
      if (!code || this.isCheckingProduct || this.productNotFoundForCode === code) return;
      if (qty <= 0) {
        alert('La cantidad debe ser mayor a 0.');
        return;
      }

      this.isCheckingProduct = true;
      try {
        const product = await resolveSaleInput({ term: code, quantity: qty });
        if (!product) {
          this.productNotFoundForCode = code;
          alert('Producto no encontrado.');
          return;
        }
        const selected = this.mapResolvedSaleProduct(product);
        if (!selected.precio || selected.precio <= 0) {
          alert(`El producto "${selected.nombre}" no tiene un precio de venta asignado.`);
          return;
        }
        this.selectedProduct = selected;
        this.productDescription = selected.nombre;
        this.productNotFoundForCode = '';
      } catch (error) {
        if (error?.response?.status === 404 || error?.status === 404) {
          this.productNotFoundForCode = code;
          alert(error?.message || 'Producto no encontrado. Revisá el selector.');
          this.abrirSelectorProductoConBusqueda();
        } else if (error?.status === 409) {
          alert(error.message);
          this.abrirSelectorProductoConBusqueda();
        } else {
          alert(error.userMessage || 'No se pudo obtener el producto.');
        }
      } finally {
        this.isCheckingProduct = false;
      }
    },
    mapResolvedSaleProduct(product) {
      const isPresentation = product?.tipo_resolucion === 'PRESENTACION' || Boolean(product?.presentacion_id);
      return {
        id: product.producto_id ?? product.id ?? null,
        presentacion_id: product.presentacion_id || null,
        tipo_presentacion: product.tipo_presentacion || null,
        codigo: product.codigo_barras || product.codigo_producto || product.sku_presentacion || '',
        nombre: product.nombre,
        unidad_medida: isPresentation ? 'presentación' : (product.unidad_medida_nombre ?? product.unidad_medida ?? ''),
        precio: Number(product.precio_venta_actual ?? product.precio_unitario_resuelto ?? product.precio_venta ?? 0),
        es_presentacion: isPresentation,
        cantidad_presentacion: product.cantidad_presentaciones ?? null,
        cantidad_unidades: product.cantidad_unidades ?? null,
        factor_conversion: product.factor_conversion ?? null,
        componentes_stock: product.impacto_stock_componentes || product.componentes_stock || [],
        ...datosGranel(product)
      };
    },
    agregarProducto() {
      if (this.readonly) return;
      if (!this.selectedProduct?.id && !this.selectedProduct?.presentacion_id) {
        alert('Primero buscá un producto válido.');
        return;
      }
      if (!this.productQuantity || this.productQuantity <= 0) {
        alert('La cantidad debe ser mayor a 0.');
        return;
      }

      this.productos.push({
        ...emptyProduct(),
        producto_id: this.selectedProduct.presentacion_id ? null : this.selectedProduct.id,
        presentacion_id: this.selectedProduct.presentacion_id || null,
        tipo_presentacion: this.selectedProduct.tipo_presentacion || null,
        codigo: this.selectedProduct.codigo,
        nombre: this.selectedProduct.nombre,
        // RF-4: una etiqueta de balanza trae el peso; esa es la cantidad (g).
        cantidad: this.selectedProduct.peso_gramos != null
          ? Number(this.selectedProduct.peso_gramos)
          : (Number(this.productQuantity) || 1),
        unidad_medida: this.selectedProduct.unidad_medida,
        precio: Number(this.selectedProduct.precio) || 0,
        es_presentacion: Boolean(this.selectedProduct.presentacion_id),
        cantidad_presentacion: this.selectedProduct.presentacion_id ? Number(this.productQuantity) || 1 : null,
        cantidad_unidades: this.selectedProduct.cantidad_unidades || null,
        factor_conversion: this.selectedProduct.factor_conversion || null,
        componentes_stock: this.selectedProduct.componentes_stock || [],
        peso_gramos: this.selectedProduct.peso_gramos ?? null,
        origen_lectura: this.selectedProduct.origen_lectura || null,
        codigo_plu_balanza: this.selectedProduct.codigo_plu_balanza || null,
        codigo_balanza_raw: this.selectedProduct.codigo_balanza_raw || null
      });

      this.productCode = '';
      this.productQuantity = 1;
      this.productDescription = '';
      this.selectedProduct = null;
    },
    async eliminarProducto(index) {
      const confirmed = await confirm({
        message: '¿Deseas eliminar este producto del pedido?',
        title: 'Eliminar producto',
        confirmText: 'Eliminar',
        cancelText: 'Mantener'
      });
      if (!confirmed) return;
      this.productos.splice(index, 1);
    },
    // detalle-venta-columnas-spec.md RF-2: granel = gramos/1000 × precio por kg,
    // redondeado al guaraní (SPEC_ESQUEMA_PRECIOS RN-3).
    subtotalLinea(producto = {}) {
      const precio = Number(producto.precio) || 0;
      if (producto.peso_gramos != null) {
        return Math.round((Number(producto.peso_gramos) / 1000) * precio);
      }
      return (Number(producto.cantidad) || 0) * precio;
    },
    // RF-3: misma presentación que la pantalla de venta.
    formatCantidad(producto = {}) {
      if (producto.peso_gramos == null) return producto.cantidad;
      const gramos = Number(producto.peso_gramos) || 0;
      return gramos >= 1000 ? `${(gramos / 1000).toFixed(3)} kg` : `${gramos} g`;
    },
    formatPrecio(producto = {}) {
      const precio = this.formateaNumero(producto.precio);
      return producto.peso_gramos != null ? `${precio} / kg` : precio;
    },
    formateaNumero(value) {
      const number = Number(value) || 0;
      return Math.round(number).toLocaleString('es-PY', { minimumFractionDigits: 0 });
    },
    badgeClass(estado) {
      const normalized = (estado || '').toLowerCase();
      if (normalized === 'pendiente') return 'bg-warning text-dark';
      if (normalized === 'completado') return 'bg-info text-dark';
      if (normalized === 'finalizado') return 'bg-success';
      if (normalized === 'cancelado') return 'bg-danger';
      return 'bg-secondary';
    }
  },
  mounted() {
    this.desprotegerSalida = salidaProtegidaService.proteger({
      hayCambios: () => !this.readonly && Boolean(this.pedido) && this.huellaPedido() !== this.huellaInicial
    });
  },
  beforeUnmount() {
    if (this.desprotegerSalida) this.desprotegerSalida();
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.order-editor {
  position: sticky;
  top: 1rem;
}

.order-editor__footer {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.order-editor__mobile-list {
  display: grid;
  gap: 0.75rem;
}

.order-editor__product-card {
  border-radius: 16px;
}

.order-editor__product-index {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8c5d1d;
  margin-bottom: 0.35rem;
}

.order-editor__product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.order-editor__product-cell {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.75rem;
}

.order-editor__product-cell .label {
  display: block;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.overlay-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.overlay-panel {
  width: min(1100px, 100%);
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}

.overlay-body {
  overflow: auto;
  max-height: calc(100vh - 8rem);
}

@media (max-width: 991.98px) {
  .order-editor {
    position: static;
  }

  .order-editor__footer {
    display: block !important;
  }

  .order-editor__footer > div + div {
    margin-top: 1rem;
  }

  .order-editor__footer .btn {
    width: 100%;
  }
}

@media (max-width: 575.98px) {
  .order-editor__product-grid {
    grid-template-columns: 1fr;
  }
}
</style>
